import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const COMPANY = "RIMITANA GLOBAL NETWORKS PRIVATE LIMITED";
const EMAIL = "rimitananetwork@gmail.com";
const PHONE = "+91 9690 181 181";
const SITE = "www.animalmela.com";

const SECTIONS = [
  {
    h: "Acceptance of Terms",
    p: [
      `These Terms & Conditions ("Terms") govern your access to and use of the AnimalMela website, Progressive Web App and Android application (together, the "Service"), operated by ${COMPANY} ("we", "our", "us").`,
      "By creating an account, signing in, or otherwise using the Service, you confirm that you have read, understood and agree to be bound by these Terms and our Privacy Policy. If you do not agree, do not use the Service.",
    ],
  },
  {
    h: "Eligibility",
    list: [
      "You must be at least 18 years of age and capable of entering into a legally binding agreement under Indian law.",
      "Pashu Haat owner (subscriber) accounts must be operated by, or with the authority of, the person or entity legally responsible for the haat.",
      "Helper accounts may be used only by staff authorised by the respective haat owner.",
    ],
  },
  {
    h: "User Accounts",
    list: [
      "Accounts are identified by your mobile number and secured by OTP and/or password.",
      "You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.",
      "Haat owners are responsible for the helpers they add, including their entries and cash records.",
      "Notify us immediately of any unauthorised use of your account; owners may remotely revoke a lost or compromised device from the Settings panel.",
    ],
  },
  {
    h: "User Responsibilities",
    list: [
      "Enter true, accurate and lawful information for sellers, purchasers, animals, payments and gate passes.",
      "Comply with all applicable laws and regulations governing livestock trade, animal welfare and market operation in your state.",
      "Obtain any consent required from sellers and purchasers before recording their personal details in the Service.",
      "Keep your device secure, especially where offline records are stored on it.",
    ],
  },
  {
    h: "Subscription & Payments",
    list: [
      "The Service is offered to haat owners on paid subscription plans; current plans and prices are displayed on our website and may be revised with prior notice.",
      "Subscription charges are payable in advance for each billing period.",
      "Applicable taxes (including GST) may be added to the listed prices.",
      "We may suspend access to paid features if a subscription lapses or payment is not received.",
    ],
  },
  {
    h: "Refund & Cancellation Policy",
    list: [
      "You may cancel your subscription at any time; cancellation stops future renewals.",
      "Fees already paid for a commenced billing period are non-refundable, except where a refund is required by applicable law or expressly agreed by us in writing.",
      "If we permanently discontinue the Service during a paid period, we will refund the unused portion of your subscription on a pro-rata basis.",
      "For refund requests, contact us using the details in the Contact Information section.",
    ],
  },
  {
    h: "Offline Mode",
    p: [
      "The Service is designed to work without a continuous internet connection. Records created offline are stored encrypted on your device and synchronised to our servers when connectivity is restored.",
      "You acknowledge that records remain on the device until synchronisation completes, and that deleting the app, clearing browser data or losing the device before sync may result in the loss of unsynced records. We are not liable for data lost before it reaches our servers.",
    ],
  },
  {
    h: "Data Accuracy",
    p: [
      "Registers, receipts, gate passes and reports produced by the Service reflect the data entered by you and your authorised users. We do not verify the identity of sellers or purchasers, the ownership, health or provenance of animals, or the correctness of amounts entered.",
      "Responsibility for the accuracy, completeness and legality of all recorded data rests with the haat owner and its users.",
    ],
  },
  {
    h: "Acceptable Use",
    p: ["You agree NOT to:"],
    list: [
      "Use the Service for any unlawful purpose, including trade in animals prohibited by law.",
      "Upload false, misleading, defamatory or infringing content.",
      "Attempt to gain unauthorised access to other tenants' data, our systems or other users' accounts.",
      "Reverse engineer, copy, resell or sublicense the Service or any part of it.",
      "Interfere with the operation of the Service, including by introducing malware or placing unreasonable load on our infrastructure.",
    ],
  },
  {
    h: "Intellectual Property Rights",
    p: [
      `The Service, including its software, design, logos, trademarks and documentation, is owned by ${COMPANY} and protected by applicable intellectual property laws. You receive a limited, non-exclusive, non-transferable licence to use the Service for your haat operations while your subscription is active.`,
      "Data you enter remains yours. You grant us a licence to host, process and back up that data solely to provide the Service.",
    ],
  },
  {
    h: "Privacy",
    p: [
      "Our collection and use of personal information is described in our Privacy Policy, which forms part of these Terms.",
    ],
    privacyLink: true,
  },
  {
    h: "Third-Party Services",
    p: [
      "The Service integrates with third-party platforms such as WhatsApp, SMS gateways, email providers and cloud hosting. We are not responsible for the availability, performance or policies of these third-party services, and your use of them is subject to their own terms.",
    ],
  },
  {
    h: "Limitation of Liability",
    p: [
      "The Service is provided on an \"as is\" and \"as available\" basis. To the maximum extent permitted by law, we disclaim all warranties, express or implied, and we shall not be liable for any indirect, incidental, special or consequential loss — including loss of profits, business, goodwill or data — arising from your use of, or inability to use, the Service.",
      "Our total aggregate liability for any claim arising out of or relating to the Service shall not exceed the subscription fees paid by you in the three (3) months preceding the event giving rise to the claim.",
    ],
  },
  {
    h: "Suspension & Termination",
    list: [
      "We may suspend or terminate access to the Service for breach of these Terms, non-payment, unlawful activity or risk to other users or our systems.",
      "You may stop using the Service and cancel your subscription at any time.",
      "Upon termination, your right to use the Service ceases; data is retained or deleted in accordance with the Data Retention terms of our Privacy Policy.",
    ],
  },
  {
    h: "Changes to Terms",
    p: [
      "We may update these Terms from time to time. Updated Terms will be posted on this page with a revised effective date, and material changes may be notified in-app or by message. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.",
    ],
  },
  {
    h: "Governing Law (India)",
    p: [
      "These Terms are governed by and construed in accordance with the laws of India. Any dispute arising out of or in connection with these Terms or the Service shall be subject to the exclusive jurisdiction of the competent courts in India.",
    ],
  },
];

export default function TermsAndConditions() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "AnimalMela Terms & Conditions";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const prevDesc = meta.content;
    meta.content = "Official Terms & Conditions for using AnimalMela services operated by RIMITANA GLOBAL NETWORKS PRIVATE LIMITED.";
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else meta.content = prevDesc;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="terms-page">
      {/* Header (hidden on print) */}
      <header className="border-b print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="terms-home-link">
            <BrandLogo size="footer" alt="AnimalMela" />
            <span className="font-display font-extrabold">AnimalMela</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900" data-testid="terms-print-btn">
              <Printer className="w-4 h-4" /> Print
            </button>
            <Link to="/login" className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900" data-testid="terms-back-login">
              <ArrowLeft className="w-4 h-4" /> Sign in
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Terms &amp; Conditions</h1>
        <p className="mt-3 text-base font-semibold">AnimalMela</p>
        <p className="text-sm text-gray-500">A Product of {COMPANY}</p>
        <p className="mt-2 text-sm text-gray-500">Effective Date: <span className="font-semibold text-gray-700">11 August 2026</span></p>

        <div className="mt-10 space-y-10">
          {SECTIONS.map((s, i) => (
            <section key={s.h} data-testid={`terms-section-${i}`}>
              <h2 className="text-lg font-bold mb-3">{i + 1}. {s.h}</h2>
              {(s.p || []).map((para, j) => (
                <p key={j} className="text-[15px] leading-7 text-gray-700 mb-3">{para}</p>
              ))}
              {s.privacyLink && (
                <p className="text-[15px] leading-7 text-gray-700 mb-3">
                  Read it here:{" "}
                  <Link to="/privacy-policy" className="text-green-700 underline underline-offset-2" data-testid="terms-privacy-link">
                    Privacy Policy
                  </Link>
                </p>
              )}
              {s.list && (
                <ul className="list-disc pl-6 space-y-2 text-[15px] leading-7 text-gray-700">
                  {s.list.map((li, j) => <li key={j}>{li}</li>)}
                </ul>
              )}
            </section>
          ))}

          <section data-testid="terms-section-contact">
            <h2 className="text-lg font-bold mb-3">{SECTIONS.length + 1}. Contact Information</h2>
            <p className="text-[15px] leading-7 text-gray-700 mb-4">
              For questions about these Terms, subscriptions or refunds, reach us at:
            </p>
            <div className="rounded-2xl border p-5 text-[15px] leading-8">
              <div><span className="font-semibold">Company:</span> {COMPANY}</div>
              <div><span className="font-semibold">Website:</span> <a href={`https://${SITE}`} className="text-green-700 underline underline-offset-2" target="_blank" rel="noreferrer">{SITE}</a></div>
              <div><span className="font-semibold">Email:</span> <a href={`mailto:${EMAIL}`} className="text-green-700 underline underline-offset-2">{EMAIL}</a></div>
              <div><span className="font-semibold">Phone:</span> <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="text-green-700 underline underline-offset-2">{PHONE}</a></div>
            </div>
          </section>
        </div>

        <footer className="mt-14 pt-6 border-t text-xs text-gray-400">
          © {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </footer>
      </main>
    </div>
  );
}
